import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Alert, Button, Card, Container, Spinner } from 'react-bootstrap';
import { SurveysAPI } from '../../API';

// Brand-neutral runtime prescreener page on surveys.dataxing.com.
//
// Mounted at three public routes (App.tsx — outside the auth shell):
//   /PreScreenQues/:surveyId/:userId         — panelist + PM-test flows
//   /PartnerPreScreenQues/:surveyId/:userId  — vendor partner flow
//   (both render this same component)
//
// Reached after either:
//   (1) Country backend redirectToSurvey hands a panelist off here when
//       survey.preQuestionsYN === 'Y'.
//   (2) Admin backend resolvePartnerEntry hands a vendor partner here when
//       the same flag is set on the survey.
//   (3) Admin testSurvey handler still uses the panelist branch with a
//       TestSurveyUsers + SurveyAssigned row.
//
// Calls admin backend's brand-neutral runtime endpoints. The evaluate
// endpoint detects panelist vs partner from the user_id (DB lookup) and
// dispatches accordingly; the FE doesn't need to branch.
//
// Why admin (not country): prescreener questions are saved to admin DB
// during survey creation, so admin's preScreen.controller is the source of
// truth. The runtime endpoints reuse that handler unchanged.

const ADMIN_API_URL = process.env.REACT_APP_BASE_URL_API || 'http://localhost:9001';

// Plain axios — no admin encryption interceptors, no auth headers. The
// runtime-prescreener-* routes are explicitly exempt from admin's encryption
// middleware (admin/backend/src/Middlewares/EncryptionDecryption.js).
const adminApi = axios.create({
    baseURL: ADMIN_API_URL,
    headers: { 'Content-Type': 'application/json' },
});

interface Question {
    id: number;
    question_id: number | string;
    question: string;
    questionType: string;     // 'Radio' | 'Checkbox' | 'checkbox' | 'Range' | 'Dropdown' | 'MultiSelectDropdown'
    answer: any;              // shape varies by questionType
    questionDisplayOrder?: number;
}

type AnswerEntry = { question: number | string; answer: any[] };

const PreScreenQuesPage: React.FC = () => {
    // userId is the canonical name now — covers panelists (users.id),
    // PM test users (TestSurveyUsers.id), and vendor partners (partnerusers.id).
    // Backwards-compat: legacy /PreScreenQues/:surveyId/:testUserId route still
    // works because we read either param name.
    const params = useParams<{ surveyId: string; userId?: string; testUserId?: string }>();
    const surveyId = params.surveyId;
    const userId = params.userId || params.testUserId;

    const [questions, setQuestions] = useState<Question[]>([]);
    const [answers, setAnswers] = useState<AnswerEntry[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (!surveyId) {
            setError('Missing surveyId in URL');
            setLoading(false);
            return;
        }
        adminApi.post('/api/v1/surveys/runtime-prescreener-questions', { survey_id: surveyId })
            .then((res) => {
                const data = (res.data && res.data.data) ? res.data.data : [];
                const sorted = Array.isArray(data)
                    ? [...data].sort((a, b) => (a.questionDisplayOrder || 0) - (b.questionDisplayOrder || 0))
                    : [];
                setQuestions(sorted);
                setAnswers(sorted.map(q => ({ question: q.id, answer: [] })));
                setLoading(false);
            })
            .catch((err) => {
                setError(err?.response?.data?.message || err?.message || 'Failed to load prescreener questions');
                setLoading(false);
            });
    }, [surveyId]);

    const currentQuestion = questions[currentIndex];
    const currentAnswerEntry = answers[currentIndex];
    const currentAnswers: any[] = currentAnswerEntry ? currentAnswerEntry.answer : [];

    const setCurrentAnswers = (next: any[]) => {
        setAnswers(prev => {
            const copy = [...prev];
            copy[currentIndex] = { question: currentQuestion.id, answer: next };
            return copy;
        });
    };

    const handleRadio = (value: any) => setCurrentAnswers([value]);
    const handleCheckbox = (value: any, checked: boolean) => {
        if (checked) setCurrentAnswers([...currentAnswers, value]);
        else setCurrentAnswers(currentAnswers.filter(v => v !== value));
    };
    const handleSelect = (value: any) => setCurrentAnswers([value]);

    // Pure per-question qualifier check. Mirrors the backend's matchAnswers
    // rule exactly (admin/backend/src/Controllers/preScreen.controller.js
    // lines 510-540) so a frontend-side early terminate produces the same
    // verdict the backend would on submit. answerId is already populated by
    // getSurveyQuestions (filtered to mark==='y' entries).
    const isAnswerQualifying = (question: Question, answer: any[]): boolean => {
        if (!question || !Array.isArray(answer) || answer.length === 0) return false;
        const correctAnswers = Array.isArray((question as any).answerId) ? (question as any).answerId : [];
        if (correctAnswers.length === 0) return true; // no qualifying config = pass-through
        const normalize = (s: any) => (typeof s === 'string' ? s.trim() : s);
        if (question.questionType === 'Range') {
            const userVal = parseFloat(answer[0]);
            if (Number.isNaN(userVal)) return false;
            return correctAnswers.some((entry: any) => {
                const range = entry && entry.answer ? entry.answer : entry;
                const from = parseFloat(range && range.fromValue);
                const to = parseFloat(range && range.toValue);
                if (Number.isNaN(from) || Number.isNaN(to)) return false;
                return userVal >= from && userVal <= to;
            });
        }
        const correctValues = correctAnswers.map((e: any) => (e && e.answer !== undefined ? e.answer : e));
        const normalizedCorrect = correctValues.map(normalize);
        return answer.some((userVal: any) => normalizedCorrect.includes(normalize(userVal)));
    };

    // Range answer is a list of {fromValue, toValue} objects in the question;
    // expand into a flat list of integers for a single-select dropdown.
    const rangeOptions = useMemo(() => {
        if (!currentQuestion || currentQuestion.questionType !== 'Range') return [];
        const ranges = Array.isArray(currentQuestion.answer) ? currentQuestion.answer : [];
        const all: number[] = [];
        ranges.forEach((r: any) => {
            const from = parseInt(r?.answer?.fromValue ?? r?.fromValue, 10);
            const to   = parseInt(r?.answer?.toValue   ?? r?.toValue,   10);
            if (!Number.isNaN(from) && !Number.isNaN(to)) {
                for (let i = from; i <= to; i++) all.push(i);
            }
        });
        return Array.from(new Set(all)).sort((a, b) => a - b);
    }, [currentQuestion]);

    const goNext = () => {
        if (!currentAnswers || currentAnswers.length === 0) {
            alert('Please choose an answer.');
            return;
        }
        // Short-circuit on the first non-qualifying answer: submit just the
        // answers walked so far. The backend's per-question loop in
        // preScreen.matchAnswers will see the failing entry and return the
        // quality-terminate URL — same response shape and hit-recording chain
        // as an end-of-flow terminate, no Q-after-this-one ever rendered.
        if (!isAnswerQualifying(currentQuestion, currentAnswers)) {
            submitAnswers(answers.slice(0, currentIndex + 1));
            return;
        }
        setCurrentIndex(i => Math.min(i + 1, questions.length - 1));
    };
    const goPrev = () => setCurrentIndex(i => Math.max(0, i - 1));

    const handleSubmit = () => {
        if (questions.length > 0 && (!currentAnswers || currentAnswers.length === 0)) {
            alert('Please choose an answer.');
            return;
        }
        // Same short-circuit applies on the final Proceed click: if the last
        // answer is non-qualifying, send only the answers walked so far so
        // the backend terminates on that entry without seeing partial later
        // questions as 'unanswered' (which would otherwise trip its
        // non-empty-answer validation).
        if (
            questions.length > 0 &&
            currentQuestion &&
            !isAnswerQualifying(currentQuestion, currentAnswers)
        ) {
            submitAnswers(answers.slice(0, currentIndex + 1));
            return;
        }
        submitAnswers(questions.length > 0 ? answers : []);
    };

    const submitAnswers = async (detailsToSubmit: AnswerEntry[]) => {
        if (!surveyId || !userId) {
            setError('Missing surveyId or userId');
            return;
        }
        setSubmitting(true);
        try {
            let verisoulSessionId = null;
            // Pass userId so the backend can resolve a vendor respondent's
            // partner mapping (partnersSurveys.verisoul) — the flag the
            // evaluator actually gates on. Asking with the survey alone read
            // the panelist-level flag and never fetched a session for
            // mapping-level Verisoul. Panelist ids fall through unchanged.
            const isVerisoulEnabled = await SurveysAPI.checkVerisoulEnabled(surveyId, undefined, userId);
            if (isVerisoulEnabled) {
                try {
                    const { getVerisoulSessionId } = await import('../../Utils/verisoulHelper');
                    verisoulSessionId = await getVerisoulSessionId();
                } catch (e) {
                    console.error("Verisoul SDK error:", e);
                }
            }

            const res = await adminApi.post('/api/v1/surveys/runtime-prescreener-evaluate', {
                survey_id: surveyId,
                user_id: userId,
                details: questions.length > 0 ? detailsToSubmit : [],
                verisoulSessionId
            });
            // matchAnswers returns the onward URL as the response body's data
            // — typically the client survey URL with placeholders substituted
            // (qualifying answer) or the terminate page URL (non-qualifying).
            const target = res?.data?.data;
            if (typeof target === 'string' && target.length > 0) {
                window.location.href = target;
            } else {
                setError('Prescreener submitted but no redirect URL was returned.');
                setSubmitting(false);
            }
        } catch (err: any) {
            setSubmitting(false);
            setError(err?.response?.data?.message || err?.message || 'Failed to submit prescreener answers');
        }
    };

    return (
        <div style={{ minHeight: '100vh', background: '#f5f5f5', padding: '40px 20px' }}>
            <Container style={{ maxWidth: '720px' }}>
                <Card>
                    <Card.Body style={{ padding: '32px' }}>
                        <div style={{ marginBottom: '24px', color: '#666', fontSize: '0.85rem' }}>
                            Survey ID: {surveyId} · User: {userId}
                        </div>

                        {loading && (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <div className="mt-3" style={{ color: '#555' }}>Loading questions…</div>
                            </div>
                        )}

                        {!loading && error && (
                            <Alert variant="danger">{error}</Alert>
                        )}

                        {!loading && !error && questions.length === 0 && (
                            <div className="text-center py-4">
                                <p>No prescreener questions configured for this survey.</p>
                                <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
                                    {submitting ? 'Proceeding…' : 'Proceed'}
                                </Button>
                            </div>
                        )}

                        {!loading && !error && questions.length > 0 && currentQuestion && (
                            <>
                                <div style={{ color: '#888', fontSize: '0.9rem', marginBottom: '8px' }}>
                                    Question {currentIndex + 1} of {questions.length}
                                </div>
                                <h5 style={{ marginBottom: '24px' }}>{currentQuestion.question}</h5>

                                {currentQuestion.questionType === 'Radio' && Array.isArray(currentQuestion.answer) && currentQuestion.answer.map((opt: any, i: number) => (
                                    <div className="form-check" key={`r-${i}`}>
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            id={`prescreen-${currentIndex}-${i}`}
                                            name={`prescreen-${currentIndex}`}
                                            checked={currentAnswers.includes(opt)}
                                            onChange={() => handleRadio(opt)}
                                        />
                                        <label className="form-check-label" htmlFor={`prescreen-${currentIndex}-${i}`}>
                                            {opt}
                                        </label>
                                    </div>
                                ))}

                                {(currentQuestion.questionType === 'Checkbox' || currentQuestion.questionType === 'checkbox') && Array.isArray(currentQuestion.answer) && currentQuestion.answer.map((opt: any, i: number) => (
                                    <div className="form-check" key={`c-${i}`}>
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`prescreen-c-${currentIndex}-${i}`}
                                            checked={currentAnswers.includes(opt)}
                                            onChange={(e) => handleCheckbox(opt, e.target.checked)}
                                        />
                                        <label className="form-check-label" htmlFor={`prescreen-c-${currentIndex}-${i}`}>
                                            {opt}
                                        </label>
                                    </div>
                                ))}

                                {currentQuestion.questionType === 'Range' && (
                                    <select
                                        className="form-control"
                                        value={currentAnswers[0] ?? ''}
                                        onChange={(e) => handleSelect(e.target.value)}
                                    >
                                        <option value="">Select option</option>
                                        {rangeOptions.map(v => (
                                            <option key={v} value={v}>{v}</option>
                                        ))}
                                    </select>
                                )}

                                {currentQuestion.questionType === 'Dropdown' && Array.isArray(currentQuestion.answer) && (
                                    <select
                                        className="form-control"
                                        value={currentAnswers[0] ?? ''}
                                        onChange={(e) => handleSelect(e.target.value)}
                                    >
                                        <option value="">Select option</option>
                                        {currentQuestion.answer.map((opt: any, i: number) => (
                                            <option key={`d-${i}`} value={opt}>{opt}</option>
                                        ))}
                                    </select>
                                )}

                                {currentQuestion.questionType === 'MultiSelectDropdown' && Array.isArray(currentQuestion.answer) && (
                                    // Use a multi-select <select multiple>; works without extra dependencies
                                    // and matches the data shape (string[]).
                                    <select
                                        multiple
                                        className="form-control"
                                        value={currentAnswers}
                                        onChange={(e) => {
                                            const next = Array.from(e.target.selectedOptions).map(o => o.value);
                                            setCurrentAnswers(next);
                                        }}
                                        style={{ minHeight: '140px' }}
                                    >
                                        {currentQuestion.answer.map((opt: any, i: number) => (
                                            <option key={`m-${i}`} value={opt}>{opt}</option>
                                        ))}
                                    </select>
                                )}

                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '32px' }}>
                                    <Button variant="secondary" onClick={goPrev} disabled={currentIndex === 0 || submitting}>
                                        Previous
                                    </Button>
                                    {currentIndex < questions.length - 1 ? (
                                        <Button variant="primary" onClick={goNext} disabled={submitting}>
                                            Next
                                        </Button>
                                    ) : (
                                        <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
                                            {submitting ? 'Submitting…' : 'Proceed'}
                                        </Button>
                                    )}
                                </div>
                            </>
                        )}
                    </Card.Body>
                </Card>
            </Container>
        </div>
    );
};

export default PreScreenQuesPage;
